import { type Dispatch, type SetStateAction, useEffect } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import isEmpty from 'lodash/isEmpty'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Radio, RadioGroup } from '@/components/ui/radio'
import PreferencesIcon from '@/modules/chatting/icons/preferences.svg'
import type { FollowingUsersFilters as FollowingUsersFiltersType } from '@/modules/chatting/types'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { classNames } from '@/utils/helpers'

const schema = z.object({
  paid: z.literal(0).or(z.literal(1)).nullable(),
  online: z.literal(0).or(z.literal(1)).nullable(),
})

type FollowingUsersFiltersFormValues = z.infer<typeof schema>

const paidOptions = [
  { value: null, label: 'All' },
  { value: 1, label: 'Paid' },
  { value: 0, label: 'Free' },
]

const onlineOptions = [
  { value: null, label: 'All' },
  { value: 1, label: 'Online' },
  { value: 0, label: 'Offline' },
]

const defaultValues = {
  paid: null,
  online: null,
}

interface Props {
  filters: FollowingUsersFiltersType
  setFilters: Dispatch<SetStateAction<FollowingUsersFiltersType>>
}

export const FollowingUsersFilters = ({ filters, setFilters }: Props) => {
  const form = useForm<FollowingUsersFiltersFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  })

  useEffect(() => {
    if (isEmpty(filters)) {
      form.reset(defaultValues)
    }
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          form.setValue(key as keyof FollowingUsersFiltersType, value)
        }
      })
    }
  }, [filters, form])

  const handleFieldChange = (name: string, value: string) => {
    const parsedValue = JSON.parse(value)

    form.setValue(name as keyof FollowingUsersFiltersFormValues, parsedValue)
    setFilters({ ...filters, [name]: parsedValue })
  }

  const [paid, online] = form.watch(['paid', 'online'])

  return (
    <Popover>
      <PopoverTrigger
        className="outline-none"
        asChild
      >
        <IconButton
          icon={PreferencesIcon}
          className={classNames({ 'text-primary-500': !isEmpty(filters) })}
        />
      </PopoverTrigger>
      <PopoverContent
        className="z-100 w-[200px] p-2"
        asChild
      >
        <form onSubmit={form.handleSubmit(setFilters)}>
          <p className="px-1.5 text-sm leading-[22px] text-secondary-300">
            Subscription
          </p>
          <Controller
            name="paid"
            control={form.control}
            render={({ field }) => (
              <RadioGroup
                value={String(paid)}
                onChange={(value) => handleFieldChange(field.name, value)}
              >
                {paidOptions.map((option) => (
                  <Radio
                    className="p-1.5"
                    key={option.value}
                    labelClassName="text-sm font-medium leading-[22px]"
                    value={String(option.value)}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            )}
          />
          <div className="my-2 w-full border-b" />
          <p className="px-1.5 text-sm leading-[22px] text-secondary-300">
            Online Status
          </p>
          <Controller
            name="online"
            control={form.control}
            render={({ field }) => (
              <RadioGroup
                value={String(online)}
                onChange={(value) => handleFieldChange(field.name, value)}
              >
                {onlineOptions.map((option) => (
                  <Radio
                    className="p-1.5"
                    key={option.value}
                    labelClassName="text-sm font-medium leading-[22px]"
                    value={String(option.value)}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            )}
          />
        </form>
      </PopoverContent>
    </Popover>
  )
}
